import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../UI/Button';

// Путь к видео напрямую, без использования импорта
const VIDEO_PATH = '/videos/background.mp4';

const HeroSection = styled.section`
  height: 100vh;
  min-height: 600px;
  position: relative;
  display: flex;
  align-items: center;
  color: white;
  padding-top: 70px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    min-height: 500px;
    padding-top: 60px;
  }

  @media (max-width: 576px) {
    height: auto;
    padding-top: 120px;
    padding-bottom: 60px;
  }
`;

const VideoBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -2;
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DarkOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: -1;
`;

const HeroContent = styled.div`
  max-width: var(--container-width);
  padding: 0 var(--container-padding);
  margin: 0 auto;
  width: 100%;
  position: relative;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-md);
  line-height: 1.2;
  
  span {
    color: var(--color-secondary);
  }
  
  @media (max-width: 992px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: center;
  }

  @media (max-width: 576px) {
    font-size: 1.8rem;
    text-align: center;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  max-width: 700px;
  margin-bottom: var(--spacing-xl);
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 576px) {
    font-size: 1.1rem;
  }
`;

const ButtonsContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 280px;
  }
`;

const HeroButton = styled(motion.div)`
  display: block;
  margin-right: 16px;
  margin-bottom: 0;
  width: 220px;
  
  &:last-child {
    margin-right: 0;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const StyledButton = styled(Button)`
  background: transparent;
  border: 2px solid white;
  color: white;
  position: relative;
  overflow: hidden;
  z-index: 1;
  transition: color 0.4s ease-in-out;
  padding: 16px 24px;
  font-size: 1.1rem;
  width: 100%;
  
  @media (max-width: 576px) {
    padding: 14px 20px;
    font-size: 1rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--color-secondary);
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: -1;
    transform: scaleX(0);
    transform-origin: left;
  }
  
  &:hover {
    color: white;
    border-color: var(--color-secondary);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const CounterContainer = styled.div`
  display: flex;
  margin-top: var(--spacing-xxl);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-top: var(--spacing-xl);
  }
  
  @media (max-width: 576px) {
    margin-top: var(--spacing-lg);
  }
`;

const CounterItem = styled(motion.div)`
  margin-right: var(--spacing-xxl);
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: var(--spacing-lg);
  }
`;

const CounterNumber = styled.h3`
  font-size: 2.5rem;
  color: var(--color-secondary);
  margin-bottom: var(--spacing-xs);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CounterText = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Hero = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 576);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <HeroSection id="home">
      <VideoBackground>
        <video autoPlay muted loop playsInline>
          <source src={VIDEO_PATH} type="video/mp4" />
        </video>
      </VideoBackground>
      <DarkOverlay />
      
      <HeroContent>
        <HeroTitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {isMobile ? (
            <>
              Купить-<span>Стрейч.РФ</span>
              <br />— ваш надежный
              <br />поставщик
              <br />стрейч-пленки
              <br />для любых нужд
            </>
          ) : (
            <>
              Купить-<span>Стрейч.РФ</span> — ваш надежный поставщик<br />
              стрейч-пленки для любых нужд
            </>
          )}
        </HeroTitle>
        
        <HeroSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Опт и розница с доставкой по всей стране. Качество, которому доверяют!
        </HeroSubtitle>
        
        <ButtonsContainer
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <HeroButton>
            <StyledButton
              variant="outlined"
              size="large"
              onClick={() => scrollToSection('catalog')}
            >
              Каталог
            </StyledButton>
          </HeroButton>
          
          <HeroButton>
            <StyledButton
              variant="outlined" 
              size="large"
              onClick={() => scrollToSection('calculator')}
            >
              Калькулятор Опта
            </StyledButton>
          </HeroButton>
          
          <HeroButton>
            <StyledButton
              variant="outlined"
              size="large"
              onClick={() => scrollToSection('contacts')}
            >
              Связаться с нами
            </StyledButton>
          </HeroButton>
        </ButtonsContainer>
        
        <CounterContainer>
          <CounterItem
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <CounterNumber>7+</CounterNumber>
            <CounterText>лет на рынке</CounterText>
          </CounterItem>
          
          <CounterItem
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            <CounterNumber>500+</CounterNumber>
            <CounterText>довольных клиентов</CounterText>
          </CounterItem>
          
          <CounterItem
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
          >
            <CounterNumber>24/7</CounterNumber>
            <CounterText>работаем без выходных</CounterText>
          </CounterItem>
        </CounterContainer>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero; 